import type {
  LegacyProductCode,
  ProductBehavior,
} from '@up2stay/up2promo-api-client';
import {
  getLocationRoutingWarning,
  type ProductFormLocation,
  type ProductRoutingMode,
} from '@up2stay/up2promo-commons/products';
import { Link2 } from 'lucide-react';
import { useState } from 'react';

import { ProductFormLocationField } from '@/core/components/products/product-form-fields/location-field';
import { useDictionary } from '@/core/providers/dictionary-provider';
import type { ProductsDict } from '@/routes/app/products/products.types';
import { ProductsSetupChoiceCard } from '@/routes/app/products-setup-wizard/products-setup-choice-card';
import { ProductsSetupLocationSocialFix } from '@/routes/app/products-setup-wizard/products-setup-location-social-fix';
import { ProductsSetupStepShell } from '@/routes/app/products-setup-wizard/products-setup-step-shell';

export function ProductsSetupStepPickLocation({
  productType,
  targetKind,
  locationId,
  onLocationChange,
  locations,
  onCreateLocation,
  onUseCustomUrl,
  onLocationsRefresh,
}: {
  productType: LegacyProductCode | undefined;
  targetKind: ProductBehavior | string;
  locationId: string | null;
  onLocationChange: (id: string | null) => void;
  locations: ProductFormLocation[];
  onCreateLocation: () => void;
  onUseCustomUrl?: () => void;
  /** Called after inline location social save so readiness re-evaluates. */
  onLocationsRefresh?: () => void;
}) {
  const dict = useDictionary();
  const productsDict = (dict as { products?: ProductsDict }).products;
  const setup = (productsDict?.setup ?? {}) as Record<
    string,
    string | undefined
  >;

  // "Standort ist … erforderlich" only after a choice was cleared, not the
  // moment the step opens (DEV-566).
  const [touched, setTouched] = useState(false);
  const selected = locations.find((l) => l.id === locationId);
  const locationMode: ProductRoutingMode = 'location';
  const warning = getLocationRoutingWarning(
    locationMode,
    productType,
    selected,
  );

  return (
    <ProductsSetupStepShell
      title={setup.pickLocationTitle ?? ''}
      description={setup.pickLocationDescription}
    >
      <ProductFormLocationField
        value={locationId}
        onChange={(id) => {
          setTouched(true);
          onLocationChange(id);
        }}
        locations={locations}
        dict={productsDict}
        knownProductType={productType}
        targetKindForReadiness={targetKind}
        required
        requiredMessage={touched ? setup.locationRequired : undefined}
        locationWarning={warning}
        warningMessages={productsDict?.edit?.routing}
        onCreateLocation={onCreateLocation}
        createLocationLabel={setup.createLocation}
        noLocationsTitle={setup.noLocationsTitle}
        noLocationsDescription={setup.noLocationsDescription}
      />

      <ProductsSetupLocationSocialFix
        productType={productType}
        location={selected}
        warning={warning}
        onSaved={() => onLocationsRefresh?.()}
      />

      {onUseCustomUrl ? (
        <ProductsSetupChoiceCard
          title={setup.switchToCustomUrl ?? setup.destinationCustomTitle ?? ''}
          description={setup.destinationCustomDescription}
          icon={Link2}
          onClick={onUseCustomUrl}
        />
      ) : null}
    </ProductsSetupStepShell>
  );
}
